import { Fragment, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import {
  Badge,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
} from '@mui/material'
import {
  AccessTime,
  CalendarToday,
  ErrorRounded,
  Notifications,
  Phone,
  Send,
} from '@mui/icons-material'
import { getDecodedToken } from '@resolvex/resolvex-sdk'
import {
  NotificationPayload,
  useGetNotificationsByIdLazyQuery,
} from 'graphql/gen-types'
import { useMutation } from '@apollo/client'
import { MARK_AS_READ_NOTIFICATION } from 'graphql/notificationGql'
import useSocket, { SocketPayload } from './utilities/useSocket'
import { URL_ACCOUNT_SERVICE } from 'utils/variables/constants'
import { SnackbarKey, useSnackbar } from 'notistack'
import { hardRedirectTo } from 'utils/helper/functions'
import { flushCaseformPendingSaves } from 'utils/caseformFlush'

type NotificationToastActionsProps = {
  snackbarKey: SnackbarKey
  openColor: 'primary' | 'secondary'
  onOpen: () => void
}

/**
 * Persistent toast actions with two-step dismiss so a single misclick
 * cannot clear the notification.
 */
const NotificationToastActions = ({
  snackbarKey,
  openColor,
  onOpen,
}: NotificationToastActionsProps): JSX.Element => {
  const { closeSnackbar } = useSnackbar()
  const [confirmDismiss, setConfirmDismiss] = useState(false)

  if (confirmDismiss) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
        <Button
          size="small"
          color="inherit"
          onClick={() => setConfirmDismiss(false)}
        >
          Keep
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => closeSnackbar(snackbarKey)}
        >
          Confirm dismiss
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
      <Button
        size="small"
        color="inherit"
        onClick={() => setConfirmDismiss(true)}
      >
        Dismiss
      </Button>
      <Button
        variant="contained"
        color={openColor}
        endIcon={<Send />}
        onClick={onOpen}
      >
        Open
      </Button>
    </Box>
  )
}

export const NotificationsBell = (): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const { clientName, id } = getDecodedToken()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isFullPageLoading, setIsFullPageLoading] = useState<boolean>(false)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    getNotifications()
    setAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const [markAsReadNotification] = useMutation(MARK_AS_READ_NOTIFICATION)

  const handleMarkAllAsRead = async () => {
    await markAsReadNotification({
      variables: {
        markAsReadNotificationArgs: {
          clientName: clientName,
        },
      },
    })

    getNotifications()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNotificationClick = async (notification: NotificationPayload) => {
    // Mark the notification as read
    // markNotificationAsRead(id) // You need to implement this function
    if (notification.createdAt) {
      await markAsReadNotification({
        variables: {
          markAsReadNotificationArgs: {
            clientName: clientName,
            timestamp: notification.createdAt,
          },
        },
      })
    }

    // Navigate to the specified URL if it exists
    if (notification.url) {
      setIsFullPageLoading(true)
      await flushCaseformPendingSaves()
      hardRedirectTo(notification.url)
    }
  }

  const [, { refetch, data, loading, error }] =
    useGetNotificationsByIdLazyQuery({
      variables: {
        getNotificationsByIdArgs: {
          clientName: clientName,
        },
      },
      fetchPolicy: 'network-only',
    })

  const getNotifications = () => {
    if (clientName && id) {
      refetch({
        getNotificationsByIdArgs: {
          clientName: clientName,
        },
      })
    }
  }

  const notificationsData: NotificationPayload[] = useMemo(() => {
    if (!loading && !error && data?.getNotificationsById) {
      return data.getNotificationsById
    }

    return []
  }, [data, loading, error])

  const unreadNotificationsCount = notificationsData.filter(
    (notification) => !notification.read
  ).length

  // Notification Code
  const socket = useSocket(`${URL_ACCOUNT_SERVICE}/notifications`)

  useEffect(() => {
    getNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!socket || !id) {
      return
    }

    socket.emit('register', id)

    socket.on('user-notification', (data: SocketPayload) => {
      if (data?.show) {
        enqueueSnackbar(
          <>
            <Typography variant="overline">{data.message} &nbsp;</Typography>
          </>,
          {
            action: (snackbarKey) => (
              <NotificationToastActions
                snackbarKey={snackbarKey}
                openColor={
                  data?.variant === 'calendar' ? 'secondary' : 'primary'
                }
                onOpen={() =>
                  handleNotificationClick({
                    createdAt: data.createdAt,
                    message: data.message,
                    title: data.message,
                    url: data.url,
                    variant: data.variant,
                  })
                }
              />
            ),
            variant: data?.variant === 'calendar' ? 'default' : 'info',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            hideIconVariant: true,
            persist: true,
            disableWindowBlurListener: true,
            style: { zIndex: 95 },
          }
        )
      }

      getNotifications()
    })

    socket.on('error', () => {
      enqueueSnackbar(
        <Typography variant="overline" color="error.contrastText">
          Something went wrong with notification service
        </Typography>,
        {
          variant: 'error',
          autoHideDuration: 5000,
          persist: false,
          disableWindowBlurListener: true,
        }
      )
      console.error('Something went wrong with the socket')
    })

    return () => {
      socket.off('user-notification')
      socket.off('connected')
      socket.off('error')
    }
  }, [socket]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Fragment>
      <Backdrop
        open={isFullPageLoading}
        sx={{
          opacity: '0.1',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Typography
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
          }}
          color="info.main"
          variant="h5"
        >
          <CircularProgress size={48} />
        </Typography>
      </Backdrop>

      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title="Notifications">
          <IconButton color="inherit" onClick={handleClick}>
            <Badge badgeContent={unreadNotificationsCount} color="error">
              <Notifications color="action" />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleMarkAllAsRead}>
          <Typography
            variant="h6"
            color="primary.main"
            noWrap={false}
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            Mark all as read
          </Typography>
        </MenuItem>
        <Box sx={{ maxHeight: 400, maxWidth: '480px', overflow: 'auto' }}>
          {notificationsData.length > 0 ? (
            notificationsData.map((notification, index) => (
              <Fragment key={index}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: 1,
                    py: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      alignItems: 'start',
                    }}
                  >
                    <Typography
                      variant="h6"
                      color={notification.read ? 'info.main' : 'info.dark'}
                      noWrap={false}
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {notification.title}
                    </Typography>
                    {!notification.read && (
                      <ErrorRounded style={{ color: '#4287f5' }} />
                    )}
                  </Box>

                  <Typography
                    variant="subtitle1"
                    color={notification.read ? 'info.main' : 'info.dark'}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                      whiteSpace: 'normal', // Allow text wrapping
                      wordBreak: 'break-word', // Break long words
                    }}
                    fontSize={
                      notification.variant === 'default' ? 'inherit' : '12px'
                    }
                  >
                    {notification.variant === 'default' && (
                      <Phone color="info" fontSize="small" />
                    )}
                    {notification.variant === 'calendar' && (
                      <CalendarToday color="info" fontSize="small" />
                    )}
                    {notification.message}
                  </Typography>

                  <Typography
                    variant="caption"
                    color={notification.read ? 'info.main' : 'info.dark'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <AccessTime color="info" fontSize="small" />
                    {notification.createdAt
                      ? `Delivered at ${new Date(
                          Number(notification.createdAt)
                        )}`
                      : 'Not delivered yet'}
                  </Typography>
                </MenuItem>
                <Divider />
              </Fragment>
            ))
          ) : (
            <MenuItem>
              <div className="notification">
                <div className="notification-content">
                  <p className="notification-title">
                    No notifications in the last 7 days
                  </p>
                </div>
              </div>
            </MenuItem>
          )}
        </Box>
      </Menu>
    </Fragment>
  )
}
